import { DEFAULT_EQUIPMENT_SOURCE, NO_GEAR_SOURCE, runsOf, type ReservationKind } from '../events/labels';
import { SYSTEM_IDS } from '../settings/systems';
import { parseRouteKm, placeCreateFields, type ChosenRoute } from '../events/place';

export type WizardDraft = {
  name: string;
  client: { id: string; name: string } | null;
  location: string;
  locationAddress: string;
  locationPoint: { lat: number; lng: number } | null;
  locationApprox: boolean;
  /** "km wg trasy" as typed; '' = count the distance. */
  routeKm: string;
  /** Spec 2026-10-22-mapy-google: the place's ids and notes, "Unikaj płatnych", the chosen route. */
  venueId?: string | null | undefined;
  googlePlaceId?: string | undefined;
  placeNotes?: string | undefined;
  avoidTolls?: boolean | undefined;
  chosenRoute?: ChosenRoute | null | undefined;
  firstDay: string;
  lastDay: string;
  firstTime: string;
  lastTime: string;
  /**
   * Days picked in the calendar with gaps between them (owner 2026-10-21 00:44: Shift / Cmd): one event, each run of
   * days one term, the hours on each. Empty: the first and last day above, as one term.
   */
  days: string[];
  scopes: string[];
  /** An entry of the systems list; '' = not chosen. */
  systemId: string;
  equipmentSource: string;
  reservationKind: ReservationKind;
  needsQuote: boolean;
  templateId: string | null;
  /** Checklists of the settings to add (owner remark 3/20). */
  checklistPresetIds: string[];
  /** "Kto widzi" (spec 2026-10-23-widocznosc-wydarzen); undefined = untouched (the server lists the creator alone). */
  viewerIds?: string[] | undefined;
};

/** P15: the last step "Sprzętówka" comes after creating the event (it works on the real packlist). */
export const STEPS = ['Klient i termin', 'Zakres', 'Szablon', 'Podsumowanie', 'Sprzętówka'] as const;
/** The step that creates the event ("Utwórz wydarzenie"). */
export const CREATE_STEP = 3;

/** Most terms of one event (as the server). */
export const MAX_TERMS = 60;

/** "Skąd sprzęt: Bez sprzętu" (P15): no packlist step, no reminder, the system "Bez systemu". */
export const noGear = (d: Pick<WizardDraft, 'equipmentSource'>): boolean => d.equipmentSource.trim().toLocaleLowerCase('pl-PL') === NO_GEAR_SOURCE.toLocaleLowerCase('pl-PL');

/** The steps of this event: without gear it ends with "Podsumowanie". */
export const stepsFor = (d: Pick<WizardDraft, 'equipmentSource'>): readonly string[] => (noGear(d) ? STEPS.slice(0, CREATE_STEP + 1) : STEPS);


/**
 * A new event starts with "Dźwięk kompletny" (owner remark 4), the default system once the list loads (3/13), a "wstępna" reservation, "Potrzebna wycena" on
 * (owner remark 3/14) and gear from our warehouse (3/15). `visible(field)`: whether the role may write the event
 * field — a hidden "Potrzebna wycena" stays off, so it is not sent.
 */
export const emptyWizard = (visible: (field: string) => boolean = () => true, day: string | null = null, picked: readonly string[] = []): WizardDraft => ({
  name: '',
  client: null,
  location: '',
  locationAddress: '',
  locationPoint: null,
  locationApprox: true,
  routeKm: '',
  // P6: a double click on a day of the calendar starts on that day (`?dzien=`)
  firstDay: isDay(day) ? day : '',
  lastDay: isDay(day) ? day : '',
  firstTime: '',
  lastTime: '',
  days: [],
  // owner 2026-10-21 00:44: several picked days (`?dni=`): one run is the first and last day, runs with gaps are terms
  ...pickedDays(picked.filter(isDay)),
  scopes: ['dzwiek'],
  systemId: '', // filled from the loaded list (`useDefaultSystem`)
  equipmentSource: DEFAULT_EQUIPMENT_SOURCE,
  reservationKind: 'soft',
  needsQuote: visible('needsQuote'),
  templateId: null,
  checklistPresetIds: [],
});

/** First and last day of the picked days, and the days themselves when they have gaps (several terms). */
function pickedDays(days: readonly string[]): Partial<WizardDraft> {
  const runs = runsOf(days);
  if (runs.length === 0) return {};
  return { firstDay: runs[0]!.first, lastDay: runs.at(-1)!.last, days: runs.length > 1 ? [...new Set(days)].sort() : [] };
}

/** A real calendar day 'YYYY-MM-DD' (e.g. from the address). */
export function isDay(v: string | null | undefined): v is string {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(v);
}

/** What the step still needs before "Dalej" (lower case, to follow "Uzupełnij: "). */
export function missingForStep(step: number, d: WizardDraft): string[] {
  const missing: string[] = [];
  if (step === 0) {
    if (d.name.trim() === '') missing.push('nazwa');
    if (d.firstDay === '') missing.push('pierwszy dzień');
    if (d.lastDay === '') missing.push('ostatni dzień');
    else if (d.firstDay !== '' && d.lastDay < d.firstDay) missing.push('ostatni dzień nie wcześniej niż pierwszy');
    else if (d.lastDay === d.firstDay && d.firstTime && d.lastTime && d.lastTime <= d.firstTime) missing.push('godzina końca po godzinie początku');
    if (!parseRouteKm(d.routeKm).ok) missing.push('km wg trasy jako liczba, np. 412,5');
    // several terms (owner 2026-10-21 00:44): at most 60, as the server (review I3)
    if (runsOf(d.days).length > MAX_TERMS) missing.push(`najwyżej ${MAX_TERMS} terminów`);
  }
  if (step === 1) {
    if (d.equipmentSource.trim() === '') missing.push('skąd sprzęt');
    if (d.scopes.length === 0) missing.push('zakres prac');
  }
  return missing;
}

const routeKm = (d: Pick<WizardDraft, 'routeKm'>): number | null => {
  const r = parseRouteKm(d.routeKm);
  return r.ok ? r.value : null;
};

/** The body of `POST /api/planning/events`: the required fields and only the optional ones that were filled. */
export function wizardBody(d: WizardDraft, visible: (field: string) => boolean = () => true): Record<string, unknown> {
  return {
    ...placeCreateFields(d, visible),
    name: d.name.trim(),
    firstDay: d.firstDay,
    lastDay: d.lastDay,
    // P15: the system is chosen in the step "Sprzętówka" (the default until then); without gear "Bez systemu"
    systemId: noGear(d) ? SYSTEM_IDS.none : d.systemId,
    equipmentSource: d.equipmentSource.trim(),
    scopes: d.scopes,
    ...(d.client ? { contractorId: d.client.id } : {}),
    ...(d.location.trim() !== '' ? { location: d.location.trim() } : {}),
    ...(d.locationAddress.trim() !== '' ? { locationAddress: d.locationAddress.trim() } : {}),
    ...(d.locationPoint ? { locationPoint: d.locationPoint } : {}),
    // "nieprecyzyjna" is the default: only "dokładna" is sent
    ...(!d.locationApprox ? { locationApprox: false } : {}),
    ...(routeKm(d) !== null ? { routeKm: routeKm(d) } : {}),
    ...(d.days.length > 1 ? { days: d.days } : {}),
    ...(d.firstTime ? { firstTime: d.firstTime } : {}),
    ...(d.lastTime ? { lastTime: d.lastTime } : {}),
    ...(d.reservationKind !== 'soft' ? { reservationKind: d.reservationKind } : {}),
    ...(d.needsQuote ? { needsQuote: true } : {}),
    ...(d.templateId ? { templateId: d.templateId } : {}),
    ...(d.checklistPresetIds.length > 0 ? { checklistPresetIds: d.checklistPresetIds } : {}),
    ...(d.viewerIds && visible('viewerIds') ? { viewerIds: d.viewerIds } : {}),
  };
}
